import React, { useRef, useContext, useEffect, useState } from "react"
import "./interactive-module.css"
import { ModuleProvider } from "./shared/ModuleProvider"
import { FullModuleView, SlideModuleView } from "./views/display"
import { useModuleContext } from "./shared/ModuleContext"
import { ProgressBar } from "react-bootstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExclamationCircle, faCheckCircle, faClock, faChevronRight, faChevronLeft, faSpinner } from "@fortawesome/pro-regular-svg-icons"
import { faSpinnerThird } from "@fortawesome/pro-duotone-svg-icons"
import { faLock } from "@fortawesome/free-solid-svg-icons"
import { useInteractiveActions } from "./shared/useInteractiveActions"
import { useQueryParam, StringParam, BooleanParam } from "use-query-params"
import useModuleDB from "./shared/useModuleDB"
import { useParams } from "@reach/router"
import LoadingSpinner from "../loading/LoadingSpinner"
import { FirebaseContext } from "../Firebase"
import Link from "../../../src/link"
import useSingleProgramDB from "./shared/useSingleProgramDB"
import { navigate } from "../../../src/link"
import { FormattedMessage, useIntl } from "react-intl"
import useWindowDimensions from "../hooks/useWindowDimensions"
import { WingsButton, WingsModal } from "@wingspans-ui"

// Matches --medium-breakpoint-down in global-styles/breakpoints.css
const COMPACT_HEADER_MAX_WIDTH = 960
// Landscape phones exceed the width breakpoint but are short
const MOBILE_MAX_HEIGHT = 500

/**
 * # Interactive Module System
 *
 * This file provides the core interactive module experience for students, educators, and
 * administrators when engaging with Wingspans educational modules.
 *
 * ## Component Architecture
 *
 * The system consists of:
 * - **InteractiveModule**: Main container managing state and data loading
 * - **InteractiveModuleSidebar**: Navigation, progress tracking, and mode switching
 * - **ModuleProvider**: Context wrapper providing module state and actions
 *
 * ## Module Modes
 *
 * The system supports three distinct rendering modes:
 *
 * ### STUDENT Mode
 * - Interactive module experience for learners
 * - Allows input responses and progress tracking
 * - Saves user progress to database
 * - Enables submission when complete
 *
 * ### SUBMISSION Mode
 * - Read-only view of completed module
 * - Shows student's submitted responses
 * - Used for review and grading
 * - Accessible after module completion
 *
 * ### PREVIEW Mode
 * - Educator preview without interaction
 * - Uses base template (no user data)
 * - No progress tracking or saving
 * - Triggered by ?preview=true query parameter
 *
 * ## Data Loading Strategy
 *
 * The system intelligently manages data based on context:
 *
 * ### Student/Interactive Mode
 * - Loads user's module record with progress
 * - Falls back to base template if no user record exists
 * - Tracks completion status and responses
 *
 * ### Preview Mode
 * - Uses only base module template
 * - No user-specific data loading
 * - Faster loading for educator preview
 *
 * ## State Management
 *
 * - **Initial State Loading**: Determines correct data source (user record vs base template)
 * - **Mode Switching**: Dynamically switches between STUDENT and SUBMISSION modes
 * - **Progress Tracking**: Monitors completion status and response data
 * - **Auto-save**: Automatically saves student progress during interaction
 *
 * ## URL Parameters
 *
 * - `moduleId`: Required - identifies the specific module
 * - `program`: Optional - associates module with specific program context
 * - `preview`: Boolean - triggers preview mode for educators
 *
 * ## Loading Sequence
 *
 * 1. Extract URL parameters and determine mode
 * 2. Load user module record (if interactive) or base template (if preview)
 * 3. Initialize module state with appropriate data
 * 4. Render InteractiveModule with ModuleProvider context
 * 5. Enable sidebar navigation and progress tracking
 *
 * ## Integration Points
 *
 * - **useModuleDB**: Manages module data loading and persistence
 * - **useInteractiveActions**: Handles user interactions and submissions
 * - **ModuleProvider**: Provides module context to child components
 * - **display.js**: Renders module content based on current mode
 *
 * For detailed component documentation, see: MODULE_COMPONENT_SYSTEM.md
 */

/**
 * Interactive Module Component
 *
 * Main component responsible for rendering the interactive module experience for students,
 * educators, and administrators. Manages module data loading, mode switching, and provides
 * the complete learning interface with navigation and progress tracking.
 *
 * Supports multiple rendering modes:
 * - STUDENT: Interactive learning experience with response tracking
 * - SUBMISSION: Read-only view of completed module responses
 * - PREVIEW: Educator preview mode without user data persistence
 *
 * @returns {JSX.Element} Complete interactive module interface with sidebar and content
 *
 * @example
 * // Route for student module access
 * <Route path="/module/:moduleId" component={InteractiveModule} />
 *
 * @example
 * // URL with query parameters
 * // /module/123?program=456&preview=true
 * // Loads module 123 in preview mode within program 456 context
 *
 * @since 1.0.0
 * @memberof CurriculumComponents
 */
const InteractiveModule = () => {
  const moduleContentRef = useRef(null)

  const { moduleId } = useParams()
  const [programId] = useQueryParam("program", StringParam)
  const [isPreviewEnabledRaw] = useQueryParam("preview", StringParam) // Use StringParam to handle trailing slash
  const cleanProgramId = programId?.replace(/\/$/, "")
  const cleanModuleId = moduleId?.replace(/\/$/, "")
  const isPreviewEnabled = isPreviewEnabledRaw?.replace(/\/$/, "") === "1" || isPreviewEnabledRaw?.replace(/\/$/, "").toLowerCase() === "true"
  const { profile, firebase } = useContext(FirebaseContext)

  // either going to be the data from the base template or the user record if it exists and is valid
  const isModuleInteractive = isPreviewEnabled ? false : true
  const { userModuleData, isModuleLoading, loadUserModuleRecord, baseModuleTemplate } = useModuleDB(moduleId, firebase, isModuleInteractive)

  // need local state for mode switching because we need to be able to switch between STUDENT and SUBMISSION modes after the module has been submitted,
  //  or use PREVIEW mode for the educator when they are viewing the module
  const [mode, setMode] = useState(isPreviewEnabled ? "PREVIEW" : "STUDENT")

  const [initialModuleState, setInitialModuleState] = useState(null)
  const [hasLoadedInitialState, setHasLoadedInitialState] = useState(false)

  // Track previous values to detect actual changes
  const prevModuleIdRef = useRef(moduleId)
  const prevIsPreviewEnabledRef = useRef(isPreviewEnabled)

  // Force reload only when moduleId or preview mode actually changes
  useEffect(() => {
    const moduleIdChanged = prevModuleIdRef.current !== moduleId
    const previewModeChanged = prevIsPreviewEnabledRef.current !== isPreviewEnabled

    if (moduleIdChanged || previewModeChanged) {
      setInitialModuleState(null)
      setHasLoadedInitialState(false)
      // Only reset mode if preview state changed or it's a new module
      if (previewModeChanged || moduleIdChanged) {
        setMode(isPreviewEnabled ? "PREVIEW" : "STUDENT")
      }
    }

    // Update refs
    prevModuleIdRef.current = moduleId
    prevIsPreviewEnabledRef.current = isPreviewEnabled
  }, [isPreviewEnabled, moduleId])

  // Inside the hook, handle the condition
  useEffect(() => {
    if (!firebase || !moduleId) {
      return // Exit early if conditions are not met
    }

    // Perform actions when conditions are met
  }, [firebase, moduleId])

  useEffect(() => {
    if (userModuleData && !hasLoadedInitialState) {
      setInitialModuleState(isPreviewEnabled ? { ...baseModuleTemplate.module_json } : { ...userModuleData.module_json })
      setHasLoadedInitialState(true)
    }
  }, [userModuleData])

  useEffect(() => {
    ;(async () => {
      const res = await loadUserModuleRecord()
      if (res?.module_json && !isPreviewEnabled) {
        setInitialModuleState({ ...res.module_json })
        setMode("STUDENT")
      }
    })()
  }, [moduleId, programId])

  if (!userModuleData?.module_json || isModuleLoading || !initialModuleState) {
    return <LoadingSpinner />
  }

  return (
    <ModuleProvider
      key={`provider-${moduleId}-${isPreviewEnabled ? "preview" : "student"}`}
      // either the base template or the user record with the module json with responses and progress data
      initialState={initialModuleState}
      // mode can be STUDENT / SUBMISSION / PREVIEW
      mode={mode}
      moduleContentRef={moduleContentRef}
      // corresponds to SQL module ID
      moduleId={cleanModuleId}
      // corresponds to SQL program ID
      programId={cleanProgramId}
      // status of the user_module in SQL table which can be in_progress, or completed
      status={userModuleData.status}
      // function for loading the user module record
      loadUserModuleRecord={loadUserModuleRecord}
    >
      <div className="interactive-module">
        <InteractiveModuleSidebar />
        <div className="interactive-module-main">
          <InteractiveModuleHeader />
          <div className="interactive-module-content">
            <div ref={moduleContentRef} className="buffer" style={{ height: "75px" }}></div>
            <div>{mode === "STUDENT" || mode === "PREVIEW" ? <SlideModuleView /> : <FullModuleView />}</div>
          </div>
          <InteractiveModuleFooter setMode={setMode} />
        </div>
      </div>
    </ModuleProvider>
  )
}

const InteractiveModuleHeader = () => {
  const { moduleState, dispatch, moduleUI } = useModuleContext()
  const { currentSectionIndex, currentSlideIndex, currentSectionSlideData, goToSlideAtIndex, mode, completionStatus } = moduleUI
  const { locale } = useIntl()
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()

  const [currentPage, setCurrentPage] = useState(0) // Track the current page
  const isCompactHeader = windowWidth <= COMPACT_HEADER_MAX_WIDTH || windowHeight <= MOBILE_MAX_HEIGHT
  const slidesPerPage = isCompactHeader ? 3 : 7 // Maximum slides per pagination section
  const totalPages = Math.ceil((currentSectionSlideData?.length || 0) / slidesPerPage) // Calculate total pages

  const currentSectionTitle =
    locale === "es-ES" ? moduleState?.sections[currentSectionIndex]?.sectionTitleEs : moduleState?.sections[currentSectionIndex]?.sectionTitle

  const renderTitle = () => {
    if (mode === "SUBMISSION") {
      return <FormattedMessage id="curriculum-submission-responses" defaultMessage="Submission Responses" />
    } else if (mode === "PREVIEW") {
      return currentSectionTitle
    } else if (completionStatus === "completed") {
      return <FormattedMessage id="curriculum-module-complete" defaultMessage="Module Complete" />
    } else {
      return currentSectionTitle
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1)
    }
  }

  const renderPaginationButtons = (currentSlideIndex) => {
    let startIndex = currentPage * slidesPerPage
    let endIndex = startIndex + slidesPerPage
    let visibleSlides = currentSectionSlideData?.slice(startIndex, endIndex) || []

    return visibleSlides.map((slide, slideIndex) => {
      const globalSlideIndex = startIndex + slideIndex
      const slidesBefore = currentSectionSlideData.slice(0, globalSlideIndex)

      if (mode === "STUDENT" && slidesBefore.some((slide) => !slide.isComplete)) {
        return (
          <button
            key={globalSlideIndex}
            className={`${currentSlideIndex === globalSlideIndex ? "active" : "disabled"}`}
            style={{ cursor: "not-allowed" }}
          >
            {globalSlideIndex + 1}
          </button>
        )
      } else {
        return (
          <button
            key={globalSlideIndex}
            className={`${currentSlideIndex === globalSlideIndex ? "active" : ""}`}
            onClick={() => goToSlideAtIndex(globalSlideIndex)}
          >
            {globalSlideIndex + 1}
          </button>
        )
      }
    })
  }

  useEffect(() => {
    const calculateCurrentPage = () => {
      const page = Math.floor(currentSlideIndex / slidesPerPage)
      setCurrentPage(page)
    }

    calculateCurrentPage()
  }, [currentSlideIndex, slidesPerPage])

  return (
    <>
      <div className="interactive-module-header">
        <div className="interactive-module-header-title">
          <h6>{renderTitle()}</h6>
        </div>

        {(mode === "PREVIEW" || (mode === "STUDENT" && completionStatus !== "completed")) && (
          <div className="interactive-module-header-pagination">
            {currentPage > 0 && (
              <button onClick={handlePreviousPage} className="pagination-chevron">
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            )}

            {renderPaginationButtons(currentSlideIndex)}

            {currentPage < totalPages - 1 && (
              <button onClick={handleNextPage} className="pagination-chevron">
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            )}
          </div>
        )}
      </div>
    </>
  )
}

const InteractiveModuleFooter = ({ setMode }) => {
  const { firebase } = useContext(FirebaseContext)
  const { moduleState, dispatch, moduleUI, programId, moduleId } = useModuleContext()
  const {
    toggleSlideView,
    goToNextSlide,
    goToPreviousSlide,
    isCurrentSlideLast,
    isCurrentSlideFirst,
    isCurrentSlideInProgress,
    isModuleComplete,
    isIncompleteSlideModalOpen,
    setIsIncompleteSlideModalOpen,
    mode,
    completionStatus,
    handleViewResponsesReset,
    moduleCompletionPercentage,
  } = moduleUI
  const { submitModule, goToNextSlideAndSaveProgress, restartModule, isSubmitting, handleManualSaveAndAlert, isSaving } =
    useInteractiveActions(firebase)
  const { programData, programModuleList = [], isProgramLoading, programCompletionPercentage } = useSingleProgramDB(programId, firebase)
  const { width } = useWindowDimensions()
  const isTouchDevice =
    typeof window !== "undefined" &&
    (window.matchMedia("(hover: none)").matches || window.matchMedia("(pointer: coarse)").matches)
  const currentModuleIndexInProgram = programModuleList.findIndex((module) => module.module_id === +moduleId)
  const upNextModuleId =
    currentModuleIndexInProgram + 1 < programModuleList.length ? programModuleList[currentModuleIndexInProgram + 1].module_id : null

  const previousModuleId = currentModuleIndexInProgram - 1 >= 0 ? programModuleList[currentModuleIndexInProgram - 1].module_id : null
  const { locale, formatMessage } = useIntl()
  const [showRestartModal, setShowRestartModal] = useState(false)

  const isModuleInProgram = !!programId
  // Same condition goToNextSlide uses to block progression
  const isNextBlocked = isCurrentSlideInProgress && mode === "STUDENT"
  // Same completion check handleModuleCompletion uses before marking the module completed
  const isSubmitBlocked = !isModuleComplete

  const displayPercentageComplete = `${isModuleInProgram ? programCompletionPercentage : moduleCompletionPercentage}%`

  const handleModeChange = (newMode) => {
    setMode(newMode)
    handleViewResponsesReset()
  }

  const handleRestartAndModeSwitch = () => {
    setShowRestartModal(false)
    restartModule()
    setMode("STUDENT")
  }

  const CompletionControls = () => (
    <>
      <WingsButton
        variant="tertiary"
        size="mini"
        onClick={() => handleModeChange(mode === "SUBMISSION" ? "STUDENT" : "SUBMISSION")}
      >
        {mode === "SUBMISSION" ? (
          <FormattedMessage id="curriculum-back" defaultMessage="Back" />
        ) : (
          <FormattedMessage id="curriculum-view-responses" defaultMessage="View Responses" />
        )}
      </WingsButton>

      <div style={{ display: "flex", gap: "1rem" }}>
        <WingsButton variant="secondary" size="mini" loading={isSubmitting} onClick={() => setShowRestartModal(true)}>
          <FormattedMessage id="curriculum-restart" defaultMessage="Restart" />
        </WingsButton>

        {upNextModuleId && (
          // <Link to={`${locale === "es-ES" ? "/es-ES" : ""}/modules/${upNextModuleId}/interactive/?program=${programId}`} style={{ textDecoration: 'none' }}>
          <Link to={`${locale === "es-ES" ? "/es-ES" : ""}/modules/${upNextModuleId}?program=${programId}`} style={{ textDecoration: "none" }}>
            <WingsButton variant="primary" size="mini">
              <FormattedMessage id="curriculum-next-module" defaultMessage="Next Module" />
            </WingsButton>
          </Link>
        )}
      </div>

      <WingsModal
        isOpen={showRestartModal}
        onClose={() => setShowRestartModal(false)}
        size="sm"
        title={<FormattedMessage id="curriculum-restart-modal-title" defaultMessage="Restart Module" />}
        secondaryActionLabel={formatMessage({ id: "curriculum-back", defaultMessage: "Back" })}
        onSecondaryAction={() => setShowRestartModal(false)}
        primaryActionLabel={formatMessage({ id: "curriculum-restart-module", defaultMessage: "Restart" })}
        onPrimaryAction={handleRestartAndModeSwitch}
      >
        <FormattedMessage
          id="curriculum-restart-modal-message"
          defaultMessage="Are you sure you want to restart this module? All previous work for this module will be deleted."
        />
      </WingsModal>
    </>
  )

  const InteractiveControls = () => (
    <>
      <div className="interactive-module-footer-left">
        {!isCurrentSlideFirst && (
          <WingsButton variant="tertiary" size="mini" iconName="chevron-left" onClick={goToPreviousSlide}>
            <FormattedMessage id="curriculum-previous" defaultMessage="Previous" />
          </WingsButton>
        )}

        {isCurrentSlideFirst && isModuleInProgram && mode === "PREVIEW" && previousModuleId && (
          <Link
            to={`${locale === "es-ES" ? "/es-ES" : ""}/modules/${previousModuleId}/?program=${programId}&preview=1`}
            style={{ textDecoration: "none" }}
          >
            <WingsButton variant="tertiary" size="mini" iconName="chevron-left">
              <FormattedMessage id="curriculum-previous-module" defaultMessage="Previous Module" />
            </WingsButton>
          </Link>
        )}

        {!isTouchDevice && width > 768 && mode === "STUDENT" && (
          <WingsButton variant="tertiary" size="mini" loading={isSaving} onClick={handleManualSaveAndAlert}>
            <FormattedMessage id="curriculum-save" defaultMessage="Save" />
          </WingsButton>
        )}
      </div>
      <div className="interactive-module-footer-right">
        {!isCurrentSlideLast && (
          <WingsButton
            variant="primary"
            size="mini"
            iconName="chevron-right"
            iconPosition="right"
            disabled={isNextBlocked}
            onClick={() => goToNextSlideAndSaveProgress()}
          >
            <FormattedMessage id="curriculum-next" defaultMessage="Next" />
          </WingsButton>
        )}

        {isCurrentSlideLast && mode === "STUDENT" && (
          <WingsButton variant="primary" size="mini" loading={isSubmitting} disabled={isSubmitBlocked} onClick={submitModule}>
            <FormattedMessage id="curriculum-submit" defaultMessage="Submit" />
          </WingsButton>
        )}

        {isCurrentSlideLast && isModuleInProgram && mode === "PREVIEW" && upNextModuleId && (
          // <Link to={`/modules/${upNextModuleId}/interactive/?program=${programId}&preview=1`}>
          // <Link to={`${locale === "es-ES" ? "/es-ES" : ""}/modules/${upNextModuleId}/interactive/?program=${programId}&preview=1`} style={{ textDecoration: 'none' }}>
          <Link
            to={`${locale === "es-ES" ? "/es-ES" : ""}/modules/${upNextModuleId}/?program=${programId}&preview=1`}
            style={{ textDecoration: "none" }}
          >
            <WingsButton variant="primary" size="mini" iconName="chevron-right" iconPosition="right">
              <FormattedMessage id="curriculum-next-module" defaultMessage="Next Module" />
            </WingsButton>
          </Link>
        )}
      </div>
    </>
  )

  return (
    <>
      <div
        className="interactive-module-footer"
        // key={`footer-${moduleState?.id}-${moduleUI?.currentSectionIndex}-${moduleUI?.currentSlideIndex}`}
      >
        {/* <div className="progress-border" style={{ width: displayPercentageComplete }} /> */}
        {(completionStatus === "completed" && mode === "STUDENT") || mode === "SUBMISSION" ? <CompletionControls /> : <InteractiveControls />}
      </div>

      <WingsModal
        isOpen={isIncompleteSlideModalOpen}
        onClose={() => setIsIncompleteSlideModalOpen(false)}
        size="sm"
        title={<FormattedMessage id="curriculum-incomplete-slide-title" defaultMessage="Slide Incomplete" />}
        primaryActionLabel={formatMessage({ id: "curriculum-incomplete-slide-confirm", defaultMessage: "Got It" })}
        onPrimaryAction={() => setIsIncompleteSlideModalOpen(false)}
      >
        <FormattedMessage
          id="curriculum-incomplete-slide-message"
          defaultMessage="Please complete the slide before moving to the next one."
        />
      </WingsModal>
    </>
  )
}

const InteractiveModuleSidebar = () => {
  const { firebase } = useContext(FirebaseContext)
  const { moduleState, moduleUI, programId, moduleId } = useModuleContext()
  const cleanProgramId = programId?.replace(/\/$/, "")
  const { currentSectionIndex, goToSectionAtIndex, sectionCompletionData, mode, completionStatus, moduleCompletionPercentage } = moduleUI
  const {
    programData,
    programModuleList = [],
    isProgramLoading,
    programCompletionPercentage,
    markCachedModuleStatusInProgram,
  } = useSingleProgramDB(cleanProgramId, firebase)
  const [hasEducatorDashboardButton, setHasEducatorDashboardButton] = useState(false)
  const { locale } = useIntl()

  const localizedModuleTitle = locale === "es-ES" ? moduleState?.titleEs : moduleState?.title
  const localizedModuleCategory = locale === "es-ES" ? moduleState?.categoryEs : moduleState?.category

  const backUrl = programId 
    ? `/programs/${programId}/${mode === "PREVIEW" ? "?preview=1" : ""}` 
    : `/modules/${moduleId}/${mode === "PREVIEW" ? "?preview=1" : ""}`

  const isModuleInProgram = !!programId

  const localizedProgramTitle = locale === "es-ES" ? programData?.title_es : programData?.title
  const localizedProgramCategory = locale === "es-ES" ? programData?.category_es : programData?.category

  // can refer to module or program completion depending on context
  const displayPercentageComplete = isModuleInProgram ? programCompletionPercentage : moduleCompletionPercentage

  const renderSectionIcon = ({ isLocked, isComplete }) => {
    if (isLocked) {
      return <FontAwesomeIcon icon={faLock} color="#CFCFCF" size="1x" />
    } else if (isComplete) {
      return <FontAwesomeIcon icon={faCheckCircle} color="#3DCC91" size="1x" />
    } else {
      return <FontAwesomeIcon icon={faSpinnerThird} color="#E89544" size="1x" />
    }
  }

  const renderCompletionStatus = (status) => {
    if (isModuleInProgram) {
      switch (programCompletionPercentage) {
        case 100:
          return (
            <label style={{ color: "#5FCCB8" }}>
              <FormattedMessage id="curriculum-completed?" defaultMessage="Completed?" />
            </label>
          )
        default:
          return (
            <label style={{ color: "#EEA539" }}>
              <FormattedMessage id="curriculum-in-progress" defaultMessage="In Progress" />
            </label>
          )
      }
    } else {
      switch (status) {
        case "completed":
          return (
            <label style={{ color: "#5FCCB8" }}>
              <FormattedMessage id="curriculum-completed" defaultMessage="Completed" />
            </label>
          )
        case "in_progress":
          return (
            <label style={{ color: "#EEA539" }}>
              <FormattedMessage id="curriculum-in-progress" defaultMessage="In Progress" />
            </label>
          )
      }
    }
  }

  const handleClick = ({ sectionIndex, isLocked }) => {
    if (isLocked) {
      return
    }
    goToSectionAtIndex(sectionIndex)
  }

  const handleModuleChange = (module) => {
    const previewParam = mode === "PREVIEW" ? "&preview=1" : ""
    navigate(`${locale === "es-ES" ? "/es-ES" : ""}/modules/${module.module_id}/interactive/?program=${programId}${previewParam}`)
  }

  const SectionTabs = () => {
    return sectionCompletionData.map((section, sectionIndex) => {
      const { sectionTitle, sectionTitleEs, isComplete, isLocked, sectionTime, sectionTimeEs } = section
      // control for the sidebar tab to be active or disabled depending on state of module
      let isActive =
        mode === "PREVIEW"
          ? sectionIndex === currentSectionIndex
          : (sectionIndex === currentSectionIndex || mode === "SUBMISSION") && completionStatus !== "completed"

      // const localizedSectionTitle = locale === "es-ES" ? sectionTitleEs : sectionTitle
      const localizedSectionTime = locale === "es-ES" ? sectionTimeEs : sectionTime

      // step 1, step 2, etc. with last section home stretch
      const getDisplayTitle = () => {
        if (sectionIndex === sectionCompletionData.length - 1) {
          return <FormattedMessage id="curriculum-home-stretch" defaultMessage="Home Stretch" />
        } else {
          return (
            <>
              <FormattedMessage id="curriculum-step" defaultMessage="Step" /> {sectionIndex + 1}
            </>
          )
        }
      }

      return (
        <div
          className={`interactive-module-sidebar-tab ${isActive ? "active" : ""}`}
          style={{ paddingLeft: isModuleInProgram ? "2.5rem" : "1.5rem" }}
          key={sectionIndex}
          role="button"
          onClick={() => handleClick({ sectionIndex, isLocked })}
        >
          {" "}
          <div className="interactive-module-sidebar-tab-program-title">
            <label role="button">
              <div>{getDisplayTitle()}</div>
              <div>
                {localizedSectionTime?.length > 0 && (
                  <span>
                    <FontAwesomeIcon icon={faClock} color="#969696" size="1x" style={{ marginRight: "5px" }} />
                    {localizedSectionTime}
                  </span>
                )}
              </div>
            </label>

            <div className="task-number">{renderSectionIcon({ isLocked, isComplete })}</div>
          </div>
          {/* {localizedSectionTime?.length > 0 && (
            <span>
              <FontAwesomeIcon icon={faClock} color="#969696" size="1x" style={{ marginRight: "5px" }} />
              {localizedSectionTime}
            </span>
          )} */}
        </div>
      )
    })
  }

  // need to sync up hooks here
  useEffect(() => {
    if (completionStatus === "completed") {
      markCachedModuleStatusInProgram(moduleId, "completed")
    } else if (completionStatus === "in_progress") {
      markCachedModuleStatusInProgram(moduleId, "in_progress")
    }
  }, [completionStatus])

  useEffect(() => {
    const checkForElementInDOM = async () => {
      // wait a second to ensure the DOM is fully loaded
      await new Promise((resolve) => setTimeout(resolve, 1000))
      if (typeof window !== "undefined" && window.document) {
        //
        const element = document.querySelector(".back-to-admin")
        if (element) {
          setHasEducatorDashboardButton(true)
        } else {
          setHasEducatorDashboardButton(false)
        }
      }
    }
    checkForElementInDOM()
  }, [])

  return (
    <>
      <div className="interactive-module-sidebar" key={`sidebar-${moduleId}`}>
        <div className="interactive-module-sidebar-info">
          <Link to={backUrl} className="interactive-module-sidebar-info-back" style={{ color: "#565656" }}>
            <h6>{isModuleInProgram ? localizedProgramTitle : localizedModuleTitle}</h6>
          </Link>
          <blockquote>{isModuleInProgram ? localizedProgramCategory : localizedModuleCategory}</blockquote>
          <br></br>

          {/* <div className="interactive-module-sidebar-info-progress">
            <div className="interactive-module-sidebar-info-progress-labels">
              <label>Status</label>
              {renderCompletionStatus(completionStatus)}
            </div>
            <ProgressBar now={displayPercentageComplete} />
          </div> */}

          <label>
            {isModuleInProgram ? (
              <span className="interactive-module-sidebar-program-overview-label">
                <FormattedMessage id="curriculum-PROGRAM-OVERVIEW" defaultMessage="PROGRAM OVERVIEW" />
              </span>
            ) : (
              <FormattedMessage id="curriculum-OVERVIEW" defaultMessage="OVERVIEW" />
            )}
          </label>
        </div>

        <div className="interactive-module-sidebar-tabs" style={{ marginBottom: hasEducatorDashboardButton ? "80px" : "0px" }}>
          {programModuleList.map((module, index) => {
            const isActive = +moduleId === +module.module_id

            const localizedModuleTitle = locale === "es-ES" ? module.module_json?.titleEs : module.title
            const localizedTime = locale === "es-ES" ? module.module_json?.timeEs : module.time
            return (
              <>
                <div
                  key={`${moduleId}-${index}`}
                  onClick={() => handleModuleChange(module)}
                  style={{
                    display: "flex",
                    gap: "10px",
                    padding: "10px 1rem",
                    fontWeight: isActive ? "bold" : "normal",
                  }}
                  role="button"
                >
                  {/* {false ? (
                    <div className="task-number">
                      <FontAwesomeIcon icon={faCheckCircle} color="#63C0D5" size="lg" />
                    </div>
                  ) : (
                    <div
                      style={{
                        border: `2px solid ${isActive ? "rgb(238, 165, 57)" : "#E0E0E0"}`,
                        border: `2px solid ${isActive ? "#58c8db" : "#FFFFFF"}`,
                        backgroundColor: isActive ? '#58c8db' : "#FFFFFF",
                        borderRadius: "50%",
                        minWidth: "30px",
                        minHeight: "30px",
                        maxHeight: "30px",
                        maxWidth: "30px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: isActive ? "#565656" : "#969696",
                      }}
                    >
                      {index + 1}
                    </div>
                  )} */}
                  <div className="interactive-module-sidebar-tab-sidebar-title" role="button" style={{ cursor: "pointer" }}>
                    <label style={{ fontWeight: "700", color: isActive ? "#565656" : "#969696", fontStyle: isActive ? "normal" : "italic" }}>
                      {" "}
                      {localizedModuleTitle}
                    </label>
                    <div style={{ fontSize: "14px", color: "#A0A0A0" }}>
                      {localizedTime && <FontAwesomeIcon icon={faClock} color="#A0A0A0" size="1x" style={{ marginRight: "5px" }} />}

                      {localizedTime}
                    </div>
                  </div>
                </div>

                {isActive && isModuleInProgram && <SectionTabs />}
              </>
            )
          })}
          {!isModuleInProgram && <SectionTabs />}
        </div>
      </div>
    </>
  )
}

export default InteractiveModule
